// ============================================================================
// FinanceSharedUI.jsx
// Componentes/hooks compartilhados pelos 3 dashboards financeiros:
//   - FINANCEIRO > Pessoal (PersonalFinance.jsx)
//   - FINANCEIRO > Escritório (OfficeFinance.jsx · FinancialManager)
//   - FINANCEIRO > Recta Scripta (RectaFinanceDashboard.jsx)
//
// Exporta:
//   - <FinanceSectionHeader /> .. faixa azul/dourada com selo do módulo + data
//   - <PendenciasUrgentesPanel /> .. 3 baldes (Vencidas / Hoje / Próximos 7d)
//   - useJsonPendencias({ escopo }) .. mina pendências do JSON canônico
//     (diario_financeiro/_db/movimentacoes_2026.json) por escopo
//
// Doutrina: Constituição V75.0 · Cap. 130-132 · Art. 184
// ============================================================================
import React, { useMemo, useState, useEffect } from 'react';
import {
  AlertTriangle, CalendarClock, CalendarDays, Wallet, PiggyBank, Printer,
  CheckCircle2, ExternalLink, Copy as CopyIcon,
} from 'lucide-react';
import { loadPrivateDataset, DATASET } from '../../../shared/api/privateDatasets';
import { ESCOPO_POR_FORNECEDOR_CNPJ } from '../../../config/FinancialSchema';

// ⚠️ SEGURANÇA (auditoria 10/09/2026): `diario_financeiro/_db/movimentacoes_2026.json`
// era IMPORTADO estaticamente aqui e compilava o DIÁRIO FINANCEIRO REAL do escritório
// (nº de conta C6, CNPJ, saldos de extrato, fornecedores) PARA DENTRO do bundle público.
// FASE 2: agora vem do Firestore
// (artifacts/portalrda-f312d/public/data/private_datasets/finance_movimentacoes),
// admin-only pela regra K do firestore.rules, lido pelo hook `useJsonPendencias` só
// depois do login. O gerador do diário grava lá via Admin SDK.

const BRAND_BLUE = '#20243B';
const HIGHLIGHT_GOLD = '#C5A059';
const HIGHLIGHT_RED = '#dc2626';

const fmtBRL = (v) => {
  if (v == null || Number.isNaN(Number(v))) return 'R$ 0,00';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const todayIso = () => new Date().toISOString().split('T')[0];

const diffDaysFromToday = (iso) => {
  if (!iso) return null;
  const today = new Date(todayIso() + 'T00:00:00');
  const target = new Date((iso.length > 10 ? iso : iso + 'T00:00:00'));
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

// ============================================================================
// Configuração de cada módulo (label, ícone, descrição)
// ============================================================================
export const MODULE_PRESETS = {
  personal: {
    label: 'Pessoal',
    selo: '💼 Finanças Pessoais — Rodrigo Demeterco',
    titulo: 'Controle de Liquidez Pessoal',
    subtitulo: 'Despesas, receitas e fluxo de caixa do titular (PF · pós-divórcio)',
    icon: PiggyBank,
    accent: HIGHLIGHT_GOLD,
  },
  office: {
    label: 'Escritório',
    selo: '⚖️ Caixa do Escritório — Rodrigo Demeterco Advocacia',
    titulo: 'Gestão Financeira do Escritório',
    subtitulo: 'Receitas, conta-cliente, despesas operacionais e movimentações da banca',
    icon: Wallet,
    accent: HIGHLIGHT_GOLD,
  },
  recta: {
    label: 'Recta Scripta',
    selo: '📊 Dashboard Financeiro — Recta Scripta Editora',
    titulo: 'Gestão Financeira da Gráfica/Editora',
    subtitulo: 'Tempo real (snapshot Bling) · Contas, propostas, faturamento e fluxo de caixa',
    icon: Printer,
    accent: HIGHLIGHT_GOLD,
  },
};

// ============================================================================
// <FinanceSectionHeader />
// Faixa brandizada azul institucional + acento dourado.
// Colocar como PRIMEIRO elemento de cada dashboard (antes dos KPIs).
// ============================================================================
export function FinanceSectionHeader({ moduleKey, monthDate, extra, badge }) {
  const preset = MODULE_PRESETS[moduleKey] || MODULE_PRESETS.personal;
  const Icon = preset.icon;

  const monthLabel = monthDate
    ? monthDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const todayLabel = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });

  return (
    <div
      className="rounded-xl p-5 text-white relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #2d3454 100%)`,
        borderLeft: `4px solid ${HIGHLIGHT_GOLD}`,
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="w-4 h-4" style={{ color: HIGHLIGHT_GOLD }} />
            <span
              className="text-[9px] font-bold uppercase tracking-widest"
              style={{ color: HIGHLIGHT_GOLD }}
            >
              {preset.selo}
            </span>
          </div>
          <h2 className="text-xl font-bold mb-0.5 truncate">{preset.titulo}</h2>
          <div className="text-[11px] text-white/70">{preset.subtitulo}</div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {badge ? (
            <span
              className="px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: HIGHLIGHT_GOLD, color: BRAND_BLUE }}
            >
              {badge}
            </span>
          ) : null}
          <div className="text-right text-[10px] text-white/80">
            <div className="font-bold uppercase tracking-wider" style={{ color: HIGHLIGHT_GOLD }}>
              {monthLabel}
            </div>
            <div className="opacity-70">{todayLabel}</div>
          </div>
        </div>
      </div>

      {extra ? <div className="mt-3 pt-3 border-t border-white/10">{extra}</div> : null}
    </div>
  );
}

// ============================================================================
// <PendenciasUrgentesPanel />
// Visual sóbrio Windows 11 Fluent: lista única ordenada por urgência, com
// faixa lateral fina (3px) indicando severidade. Tipografia clean, paleta
// neutra, sem 3 colunas chamativas.
//
// Severidade derivada do delta de dias até vencimento:
//   - delta < 0  → critical (vermelho)
//   - delta == 0 → warning (laranja)
//   - delta <= 7 → info (azul-acinzentado)
//
// Heurística "via PJ Recta" (Art. 185): se a descrição contém marcadores
// como "via PJ", "retirada do sócio", "Recta Scripta" ou se item.account
// indica PJ Recta, mostra badge sutil. Não altera a fonte de verdade.
// ============================================================================
const PJ_RECTA_PATTERNS = [
  /retirada\s+do\s+s[óo]cio/i,
  /via\s+pj/i,
  /recta\s*scripta/i,
  /art\.?\s*185/i,
];

function detectPaidViaPJRecta(item) {
  if (!item) return false;
  if (item.paidViaScope === 'recta_pj' || item.paidViaScope === 'recta') return true;
  const text = `${item.descricao || ''} ${item.account || ''} ${item.origem || ''}`;
  return PJ_RECTA_PATTERNS.some((rx) => rx.test(text));
}

const SEVERITY_TONES = {
  critical: { stripe: '#dc2626', label: 'Vencido', tagBg: '#FEE2E2', tagFg: '#991B1B' },
  warning:  { stripe: '#d97706', label: 'Hoje',    tagBg: '#FEF3C7', tagFg: '#92400E' },
  info:     { stripe: '#2563eb', label: 'Próximo', tagBg: '#DBEAFE', tagFg: '#1E40AF' },
};

function classifyItem(it) {
  const delta = diffDaysFromToday(it.vencimento);
  if (delta == null) return null;
  let severity = 'info';
  if (delta < 0) severity = 'critical';
  else if (delta === 0) severity = 'warning';
  else if (delta <= 7) severity = 'info';
  else return null; // fora da janela urgente
  return { ...it, delta, severity };
}

export function PendenciasUrgentesPanel({ items = [], onMarkPaid, onCopyLine }) {
  const list = useMemo(() => {
    const out = [];
    for (const it of items) {
      const classified = classifyItem(it);
      if (classified) out.push(classified);
    }
    // Ordenar: critical (mais antigo primeiro) > warning > info (mais próximo primeiro)
    const order = { critical: 0, warning: 1, info: 2 };
    out.sort((a, b) => order[a.severity] - order[b.severity] || a.delta - b.delta);
    return out;
  }, [items]);

  const totals = useMemo(() => {
    const acc = { critical: 0, warning: 0, info: 0, total: 0, totalValor: 0 };
    for (const it of list) {
      acc[it.severity] += 1;
      acc.total += 1;
      acc.totalValor += Number(it.valor) || 0;
    }
    return acc;
  }, [list]);

  if (list.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <div className="text-[12px] text-slate-600">
          <span className="font-semibold text-slate-800">Sem pendências urgentes.</span>{' '}
          <span className="text-slate-500">Nenhuma conta vencida, vencendo hoje ou nos próximos 7 dias.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Cabeçalho discreto */}
      <div className="px-4 py-2 flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          <span className="text-[12px] font-semibold text-slate-700">Lembretes &amp; contas a pagar</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          {totals.critical > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SEVERITY_TONES.critical.stripe }} />
              {totals.critical} vencida{totals.critical === 1 ? '' : 's'}
            </span>
          )}
          {totals.warning > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SEVERITY_TONES.warning.stripe }} />
              {totals.warning} hoje
            </span>
          )}
          {totals.info > 0 && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SEVERITY_TONES.info.stripe }} />
              {totals.info} próx. 7d
            </span>
          )}
          <span className="text-slate-300">·</span>
          <span className="font-mono font-semibold text-slate-700 tabular-nums">{fmtBRL(totals.totalValor)}</span>
        </div>
      </div>

      {/* Lista única */}
      <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
        {list.map((it) => (
          <PendenciaRow
            key={`${it.fonte}-${it.id}`}
            item={it}
            onMarkPaid={onMarkPaid}
            onCopyLine={onCopyLine}
          />
        ))}
      </div>
    </div>
  );
}

function PendenciaRow({ item, onMarkPaid, onCopyLine }) {
  const tone = SEVERITY_TONES[item.severity] || SEVERITY_TONES.info;
  const isJson = item.fonte === 'json';
  const viaPJRecta = detectPaidViaPJRecta(item);

  const dataLabel = item.vencimento
    ? new Date(item.vencimento + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : '—';
  const deltaLabel = item.delta < 0
    ? `${Math.abs(item.delta)}d em atraso`
    : item.delta === 0
      ? 'vence hoje'
      : `em ${item.delta} ${item.delta === 1 ? 'dia' : 'dias'}`;

  const handleCopy = () => {
    if (item.linhaDigitavel) {
      try { navigator.clipboard.writeText(item.linhaDigitavel); } catch { /* noop */ }
      if (typeof onCopyLine === 'function') onCopyLine(item.linhaDigitavel);
    }
  };

  return (
    <div
      className="relative px-4 py-2 hover:bg-slate-50 transition-colors group"
      title={item.origem || ''}
    >
      {/* Faixa lateral de severidade (3px) */}
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: tone.stripe }}
        aria-hidden="true"
      />

      <div className="flex items-center justify-between gap-3 pl-2">
        {/* Esquerda: descrição + meta */}
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-medium text-slate-800 leading-snug truncate" title={item.descricao}>
            {item.descricao}
          </div>
          <div className="flex items-center gap-2 text-[10.5px] text-slate-500 mt-0.5 flex-wrap">
            <span className="font-mono tabular-nums text-slate-600">{dataLabel}</span>
            <span className="text-slate-300">·</span>
            <span style={{ color: tone.stripe }} className="font-semibold">{deltaLabel}</span>
            {item.grupo ? (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{item.grupo}</span>
              </>
            ) : null}
            {viaPJRecta ? (
              <span
                className="px-1.5 py-px rounded text-[9.5px] font-medium"
                style={{ backgroundColor: '#EEF2FF', color: '#3730A3' }}
                title="Despesa pessoal paga via PJ Recta Scripta (Art. 185)"
              >
                via PJ Recta
              </span>
            ) : null}
            {isJson ? (
              <span
                className="px-1.5 py-px rounded text-[9.5px] font-medium bg-slate-100 text-slate-600"
                title="Origem: diário canônico (movimentacoes_2026.json)"
              >
                diário
              </span>
            ) : null}
          </div>
          {item.sugestaoAcao ? (
            <div className="text-[10px] text-slate-400 italic mt-0.5">→ {item.sugestaoAcao}</div>
          ) : null}
        </div>

        {/* Direita: valor + ações */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-[13px] font-mono font-semibold tabular-nums text-slate-800">
              {fmtBRL(item.valor)}
            </div>
          </div>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {item.linhaDigitavel ? (
              <button
                type="button"
                onClick={handleCopy}
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1 rounded"
                title="Copiar linha digitável"
              >
                <CopyIcon className="w-3.5 h-3.5" />
              </button>
            ) : null}
            {item.urlExterna ? (
              <a
                href={item.urlExterna}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1 rounded"
                title="Abrir externamente"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : null}
            {!isJson && typeof onMarkPaid === 'function' ? (
              <button
                type="button"
                onClick={() => onMarkPaid(item)}
                className="text-[10px] font-medium px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                title="Marcar como pago"
              >
                Marcar pago
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// useJsonPendencias({ escopo })
// Mina pendências estruturadas do JSON canônico (movimentacoes_2026.json).
//
// Hoje suporta:
//   - movimentacoes[].boletos_eloprint_status (RSE-2026-04-30-008)
//
// Futuro: outros tipos de array estruturada de boletos a vencer.
//
// escopo: 'recta' | 'personal' | 'office' | 'all'
//   - Eloprint é fornecedor da Recta Scripta Editora -> aparece em 'recta' e 'all'.
//   - Outros mapeamentos futuros podem usar campo `escopo` na própria entrada do JSON.
// ============================================================================
export function useJsonPendencias({ escopo = 'all' } = {}) {
  // FASE 2: carrega o diário financeiro do Firestore (admin-only) uma vez por sessão.
  const [movimentacoesCanonicas, setMov] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadPrivateDataset(DATASET.FINANCE_MOVIMENTACOES)
      .then((json) => { if (!cancelled) setMov(json); })
      .catch(() => { if (!cancelled) setMov({ movimentacoes: [] }); });
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const out = [];
    const movs = movimentacoesCanonicas?.movimentacoes || [];

    for (const m of movs) {
      const escopoEntrada = inferirEscopoEntrada(m);
      if (escopo !== 'all' && escopoEntrada !== escopo && escopoEntrada !== 'unknown') continue;

      // boletos_eloprint_status (RSE-2026-04-30-008)
      const arr = Array.isArray(m.boletos_eloprint_status) ? m.boletos_eloprint_status : [];
      for (const b of arr) {
        const isPendente = b.status === 'VENCIDO_NAO_PAGO' || b.status === 'FUTURO_PENDENTE';
        if (!isPendente) continue;
        // mapear linha digitável a partir de boletos[] (lista canônica) -> não disponível direto aqui
        out.push({
          id: `${m.id_local}__nf${b.nf}__p${b.parc}`,
          descricao: `NF ${b.nf} parc ${b.parc} · ${m.fornecedor?.razao_social?.split(' - ').pop() || 'Fornecedor'}`,
          valor: b.valor_base,
          vencimento: b.venc,
          grupo: 'BOLETO',
          fonte: 'json',
          origem: `Diário canônico · ${m.id_local}`,
          sugestaoAcao: b.acao === 'PAGAR_URGENTE' ? 'Pagar agora no C6 PJ (boleto via linha digitável)' : 'Agendar no C6 PJ',
          linhaDigitavel: b.linha_digitavel || null,
          escopo: escopoEntrada,
        });
      }
    }
    return out;
  }, [escopo, movimentacoesCanonicas]);
}

function inferirEscopoEntrada(m) {
  // 1) campo explícito (futuro)
  if (m.escopo) return m.escopo;
  // 2) fornecedor conhecido
  const cnpj = m.fornecedor?.cnpj;
  if (cnpj && ESCOPO_POR_FORNECEDOR_CNPJ[cnpj]) return ESCOPO_POR_FORNECEDOR_CNPJ[cnpj];
  // 3) tipos doutrinários conhecidos
  const tipo = (m.tipo || '').toLowerCase();
  if (tipo.includes('triagem_documental') && cnpj && ESCOPO_POR_FORNECEDOR_CNPJ[cnpj]) {
    return ESCOPO_POR_FORNECEDOR_CNPJ[cnpj];
  }
  return 'unknown';
}

// ============================================================================
// Helpers para os callers normalizarem postings em items do painel
// ============================================================================
export function postingsToPendencyItems(postings = [], { fonte = 'firestore', module = 'personal' } = {}) {
  return (postings || [])
    .filter((p) => p && (p.status === 'PENDING' || p.status === 'OPEN' || p.status === 'Aberto'))
    .map((p) => ({
      id: p.id || `${p.date}-${p.description}`,
      descricao: p.description || 'Lançamento sem descrição',
      valor: Number(p.value) || 0,
      vencimento: p.date,
      grupo: p.group || p.category || 'DESPESA',
      fonte,
      origem: `Firestore · ${module === 'personal' ? 'personal_postings' : 'financial_ledger'}`,
    }));
}

// Bling contas_pagar (snapshot) -> pendency items
export function blingContasPagarToPendencyItems(items = []) {
  return (items || [])
    .filter((c) => c && c.vencimento)
    .map((c) => ({
      id: `bling-${c.id || c.numeroDocumento || c.vencimento + '-' + c.valor}`,
      descricao: c.descricao || c.historico || `Conta a pagar Bling #${c.numeroDocumento || c.id}`,
      valor: Number(c.valor) || 0,
      vencimento: typeof c.vencimento === 'string' ? c.vencimento.slice(0, 10) : null,
      grupo: 'BLING',
      fonte: 'firestore', // do ponto de vista do painel, é externo (não-JSON)
      origem: `Bling · conta a pagar #${c.numeroDocumento || c.id}`,
      urlExterna: c.urlBling || null,
    }));
}
